import axios from 'axios';
import axiosRetry from 'axios-retry';
import qs from 'qs';

import { LanguageEnums, LocalStorageEnums } from 'src/shared/enums';
import {
  clearLocalStorageValues,
  getUtcOffset,
  mapLanguageIdEnums,
  redirectToLogin,
  refreshToken,
} from 'src/shared/utils';
import store from 'src/store';
import { setIsInMaintenanceActionCreator } from 'src/store/user/user.slice';

// ─────────────────────────────────────────────────────────────
// Refresh Queue Management
// ─────────────────────────────────────────────────────────────
type QueueEntry = {
  resolve: () => void;
  reject: (reason?: unknown) => void;
};

let isRefreshing = false;
let refreshQueue: QueueEntry[] = [];

function flushQueue(success: boolean) {
  refreshQueue.forEach((p) => (success ? p.resolve() : p.reject()));
  refreshQueue = [];
}

function waitForRefresh(): Promise<void> {
  return new Promise((resolve, reject) => {
    refreshQueue.push({ resolve, reject });
  });
}

// ─────────────────────────────────────────────────────────────
// Axios Instance
// ─────────────────────────────────────────────────────────────
const http = axios.create({
  paramsSerializer: (params: Record<string, string | string[] | number | number[]>) => {
    return qs.stringify(params, { arrayFormat: 'comma', encode: false });
  },
});

axiosRetry(http, {
  retries: 2,
  shouldResetTimeout: true,
  retryCondition: (error) => {
    const notRetryableCodes = [400, 401, 403, 404, 405, 422, 500];
    return Boolean(
      error.code === 'ECONNABORTED' || (error.response && !notRetryableCodes.includes(error.response.status)),
    );
  },
});

http.defaults.timeout = 60000;
http.defaults.withCredentials = true;
http.defaults.headers.common['Content-Type'] = 'application/json';

// ─────────────────────────────────────────────────────────────
// Request Interceptor
// ─────────────────────────────────────────────────────────────
http.interceptors.request.use((request) => {
  const storedAppRegion = localStorage.getItem(LocalStorageEnums.APP_REGION);
  const storedLanguageValue = localStorage.getItem(LocalStorageEnums.LANGUAGE_KEY);

  if (!request.headers) return request;

  // Accept-Language
  try {
    request.headers['Accept-Language'] = storedLanguageValue
      ? JSON.parse(storedLanguageValue)
      : mapLanguageIdEnums[LanguageEnums.EN];
  } catch {
    request.headers['Accept-Language'] = mapLanguageIdEnums[LanguageEnums.EN];
  }

  // TimeZoneOffset + AppRegion
  request.headers['TimeZoneOffset'] = getUtcOffset();

  if (storedAppRegion) {
    request.headers['AppRegion'] = storedAppRegion;
  }

  return request;
});

// ─────────────────────────────────────────────────────────────
// Response Interceptor
// ─────────────────────────────────────────────────────────────
http.interceptors.response.use(
  (response) => response,

  async (error) => {
    const originalConfig = error.config;

    // Maintenance Mode (503)
    if (error?.response?.status === 503) {
      store?.dispatch(setIsInMaintenanceActionCreator(true));
    }

    // Cancelled request
    if (axios.isCancel(error)) {
      return Promise.reject({ status: 499, data: null });
    }

    // Only handle 401 Unauthorized
    if (error.response?.status !== 401) {
      return Promise.reject({
        status: error.response?.status,
        data: error.response?.data,
      });
    }

    // Prevent refresh on the refresh endpoint itself
    if (originalConfig.url?.includes('/refresh')) {
      handleUnauthenticated();
      return Promise.reject(error);
    }

    // Already retried → logout
    if (originalConfig._retry) {
      handleUnauthenticated();
      return Promise.reject(error);
    }

    originalConfig._retry = true;

    // If refresh is already in progress, wait for it
    if (isRefreshing) {
      try {
        await waitForRefresh();
        return http(originalConfig);
      } catch {
        return Promise.reject({ status: 401, data: null });
      }
    }

    // Start token refresh
    isRefreshing = true;

    try {
      const refreshed = await refreshToken();

      if (!refreshed) {
        throw new Error('Refresh failed');
      }

      flushQueue(true);
      return http(originalConfig);
    } catch (err) {
      flushQueue(false);
      handleUnauthenticated();
      return Promise.reject(err);
    } finally {
      isRefreshing = false;
    }
  },
);

// ─────────────────────────────────────────────────────────────
// Logout Handler
// ─────────────────────────────────────────────────────────────
function handleUnauthenticated() {
  clearLocalStorageValues([LocalStorageEnums.THEME_MODE]);

  refreshQueue = [];
  isRefreshing = false;

  if (!window.location.pathname.includes('/login')) {
    redirectToLogin();
  }
}

export default http;
